export interface Attachment {
  id: string;
  card_id: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  kind: 'file' | 'image';
  width: number | null;
  height: number | null;
  /** A verified raster image: `preview` works (and it can be a cover). */
  previewable: boolean;
  created_at: string;
}

export type PreviewVariant = 'thumb' | 'medium' | 'large' | 'original';

/** Same limit as the backend (`ZADOK_MEDIA_MAX_UPLOAD_BYTES`); the server enforces it anyway. */
export const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

/** Files are always fetched through the API (authorized per request), never by storage path. */
export const attachmentDownloadUrl = (attachmentId: string) =>
  `/api/v1/attachments/${attachmentId}/download`;

export const attachmentPreviewUrl = (attachmentId: string, variant: PreviewVariant = 'medium') =>
  `/api/v1/attachments/${attachmentId}/preview?variant=${variant}`;

export type UploadStatus = 'uploading' | 'done' | 'error';

/** One file in the upload list (kept only while the card dialog is open). */
export interface UploadItem {
  key: string;
  name: string;
  size: number;
  /** 0..1 */
  progress: number;
  status: UploadStatus;
  error?: unknown;
}

export type FileCategory =
  'image' | 'pdf' | 'archive' | 'text' | 'audio' | 'video' | 'document' | 'other';

/** Icon family of a file, from its detected type. */
export function fileCategory(mime: string, filename: string): FileCategory {
  if (mime.startsWith('image/')) return 'image';
  if (mime === 'application/pdf') return 'pdf';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime.startsWith('video/')) return 'video';
  if (
    mime === 'application/zip' ||
    mime === 'application/gzip' ||
    mime.includes('compressed') ||
    mime.includes('rar') ||
    /\.(zip|rar|7z|tar|gz|tgz)$/i.test(filename)
  ) {
    return 'archive';
  }
  if (
    mime.includes('wordprocessingml') ||
    mime.includes('spreadsheetml') ||
    mime.includes('presentationml') ||
    mime.includes('opendocument') ||
    mime === 'application/msword' ||
    /\.(docx?|xlsx?|pptx?|odt|ods|odp)$/i.test(filename)
  ) {
    return 'document';
  }
  if (mime.startsWith('text/') || mime === 'application/json') return 'text';
  return 'other';
}

export function formatBytes(bytes: number, locale: string): string {
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const digits = unit === 0 || value >= 100 ? 0 : 1;
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: digits }).format(value)} ${units[unit]}`;
}
